import React from "react";
import {
    BorderMenuPage,
    DisplayMenuItem,
    Menu,
    MenuContentPage,
    MenuElement,
    contentMenuList,
    emptyMenuPage,
    typeDailyMenu,
} from "../../../../components/types/types";
import { isNull } from "lodash";

export const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

export const roundToHalf = (n: number) =>
    Math.round((n + Number.EPSILON) * 2) / 2;

export const isWhiteColor = (value: string) => value === "#ffffff";

export const buildHexInputValue = (hex: string) =>
    hex.replace(/^#/, "");

export const isValidHex6 = (value: string) =>
    /^[0-9a-fA-F]{6}$/.test(value);

export const sanitizeHexInput = (value: string) =>
    value.trim().replace(/[^0-9a-fA-F]/g, "").slice(0, 6);

export const cloneMenuElement = <T extends MenuElement>(element: T): T => ({
    ...element,
    props: { ...element.props },
});

export const cloneMenuPage = (page: MenuContentPage): MenuContentPage => ({
    ...page,
    elements: page.elements.map((el) => cloneMenuElement(el)),
});

export const cloneDobbleMenu = (
    dobbleMenu: [MenuContentPage, MenuContentPage][]
): [MenuContentPage, MenuContentPage][] =>
    dobbleMenu.map(([front, back]) => [
        cloneMenuPage(front),
        cloneMenuPage(back),
    ]);

export interface GlobalPageStyleConfig {
    currentStylePers?: { url: string; iscropp: boolean } | null;
    currentFontText?: string | null;
    currentFontTitle?: string | null;
    visiblePagesEffect?: boolean;
    colorPagesEffect?: string | null;
    colorLinePagesEffect?: string | null;
    currentColorBackPage?: string | null
}
const hasPageVisualStyle = (page: MenuContentPage): boolean => {
    return !!(
        page.imgback ||
        page.colorback ||
        page.fontfamilytext ||
        page.fontfamilytitle ||
        page.borderpage?.transparent ||
        page.borderpage?.color ||
        page.borderpage?.colorline
    );
};
const shouldKeepPage = (page: MenuContentPage): boolean => {
    return page.elements.length > 0 || hasPageVisualStyle(page);
};
export const buildSharedPagePatch = ({
    currentStylePers,
    currentFontText,
    currentFontTitle,
    visiblePagesEffect = true,
    colorPagesEffect,
    colorLinePagesEffect,
    currentColorBackPage
}: GlobalPageStyleConfig) => {
    const patch = currentStylePers ? { imgback: currentStylePers } : {};
    const patchFont = currentFontText
        ? { fontfamilytext: currentFontText }
        : {};
    const patchFontTitle = currentFontTitle
        ? { fontfamilytitle: currentFontTitle }
        : {};
    const patchColor = currentColorBackPage
        ? { colorback: currentColorBackPage }
        : {};
    const hasBorderConfig =
        !visiblePagesEffect || !!colorPagesEffect || !!colorLinePagesEffect;

    const patchBorderpage = hasBorderConfig
        ? {
              borderpage: {
                  ...(!visiblePagesEffect ? { transparent: true } : {}),
                  ...(colorPagesEffect ? { color: colorPagesEffect } : {}),
                  ...(colorLinePagesEffect
                      ? { colorline: colorLinePagesEffect }
                      : {}),
              } as BorderMenuPage,
          }
        : {};

    return {
        ...patch,
        ...patchFont,
        ...patchFontTitle,
        ...patchBorderpage,
        ...patchColor
    };
};

export const buildEmptyStyledPage = (
    config: GlobalPageStyleConfig
): MenuContentPage => ({
    ...emptyMenuPage,
    ...buildSharedPagePatch(config),
});

export const buildEmptyStyledSpread = (
    config: GlobalPageStyleConfig
): [MenuContentPage, MenuContentPage] => [
    buildEmptyStyledPage(config),
    buildEmptyStyledPage(config),
];

export interface ParsedMenuResult {
    dobbleMenu: [MenuContentPage, MenuContentPage][];
    maxIndex: number;
    currentZIndex: number;
    currentStylePers: { url: string; iscropp: boolean } | null;
    currentFontText: string | null;
    currentFontTitle: string | null;
    visiblePagesEffect: boolean;
    colorPagesEffect: string | null;
    colorLinePagesEffect: string | null;
    borderStyleVars: React.CSSProperties;
    listLibImg: string[];
    currentColorBackPage:string,
}

export const collectImageUrls = (menu: Menu): string[] => {
    const urls = new Set<string>();

    for (const page of menu) {

        for (const el of page.elements) {
            if (el.type === "img" && el.props.url) {
                urls.add(el.props.url);
            }
        }
    }

    return [...urls];
};

export const parseMenuToDobbleMenu = (menu: Menu): ParsedMenuResult => {
    if (!menu || menu.length === 0 || !menu[0]) {
        const empyMenuPageUse = {
            elements: [],
            colorback: '#ffffff'
        }
        const emptySpread: [MenuContentPage, MenuContentPage][] = [
            [empyMenuPageUse, empyMenuPageUse],
            [empyMenuPageUse, empyMenuPageUse],
        ];

        return {
            dobbleMenu: emptySpread,
            maxIndex: 2,
            currentZIndex: 2,
            currentStylePers: null,
            currentFontText: null,
            currentFontTitle: null,
            visiblePagesEffect: true,
            colorPagesEffect: null,
            colorLinePagesEffect: null,
            borderStyleVars: {},
            listLibImg: [],
            currentColorBackPage:'#ffffff'
        };
    }

    let currentStylePers: { url: string; iscropp: boolean } | null = null;
    let currentFontText: string | null = null;
    let currentFontTitle: string | null = null;
    let visiblePagesEffect = true;
    let colorPagesEffect: string | null = null;
    let colorLinePagesEffect: string | null = null;
    let borderStyleVars: React.CSSProperties = {};
    let currentColorBackPage: string | null = null

    const firstPage = menu[0];

    if (firstPage.imgback) {
        currentStylePers = firstPage.imgback;
    }
    if (firstPage.colorback) {
        currentColorBackPage = firstPage.colorback;
    }

    if (firstPage.fontfamilytext) {
        currentFontText = firstPage.fontfamilytext;
    }

    if (firstPage.fontfamilytitle) {
        currentFontTitle = firstPage.fontfamilytitle;
    }

    if (firstPage.borderpage) {
        const { transparent, color, colorline } = firstPage.borderpage;

        if (transparent) {
            visiblePagesEffect = false;
        }

        if (color) {
            colorPagesEffect = color;
            (borderStyleVars as any)["--qr-border-bg-strong"] = color;
            (borderStyleVars as any)["--qr-border-bg-soft"] = color;
        }

        if (colorline) {
            colorLinePagesEffect = colorline;
            (borderStyleVars as any)["--qr-border-line"] = colorline;
        }
    }

    const patch = buildSharedPagePatch({
        currentStylePers,
        currentFontText,
        currentFontTitle,
        visiblePagesEffect,
        colorPagesEffect,
        colorLinePagesEffect,
        currentColorBackPage
    });

    const front: MenuContentPage = { ...emptyMenuPage, ...patch };
    const back: MenuContentPage = { ...emptyMenuPage, ...patch };

    let groups: [MenuContentPage, MenuContentPage][] = [[emptyMenuPage, firstPage]];

    for (let i = 1; i < menu.length; i += 2) {
        const group: [MenuContentPage, MenuContentPage] = [
            { ...front },
            { ...back },
        ];
        if (menu[i]) {
            group[0] = menu[i];
        }

        if (i + 1 < menu.length && menu[i + 1]) {
            group[1] = menu[i + 1];
        } else if (currentStylePers) {
            group[1].imgback = currentStylePers;
        }

        groups.push(group);
    }

    if (groups.length === 1) {
        groups = [...groups, [{ ...front }, { ...back }]];
    }

    return {
        dobbleMenu: groups,
        maxIndex: groups.length * 2,
        currentZIndex: groups.length * 2,
        currentStylePers,
        currentFontText,
        currentFontTitle,
        visiblePagesEffect,
        colorPagesEffect,
        colorLinePagesEffect,
        borderStyleVars,
        listLibImg: [...new Set(collectImageUrls(menu))],
        currentColorBackPage: currentColorBackPage ?? "#ffffff"
    };
};
export const reconstructMenuFromDobble = (
    dobbleMenu: [MenuContentPage, MenuContentPage][]
): Menu => {
    const reconstructedMenu: Menu = [];
    if (dobbleMenu.length === 0) return reconstructedMenu;
    if ((dobbleMenu[0][1] && shouldKeepPage(dobbleMenu[0][1])) || (dobbleMenu.length > 0 && dobbleMenu[0][1] && dobbleMenu[0][1].elements.length > 0)) {
        reconstructedMenu.push(dobbleMenu[0][1]);
    }

    for (let i = 1; i < dobbleMenu.length; i++) {
        const [first, second] = dobbleMenu[i];

        if (first.elements.length > 0) {
            reconstructedMenu.push(first);
        }

        if (second.elements.length > 0) {
            reconstructedMenu.push(second);
        }
    }

    return reconstructedMenu;
};
export const buildVisitorDobbleMenu = (
    dobbleMenu: [MenuContentPage, MenuContentPage][],
    config: GlobalPageStyleConfig
): [MenuContentPage, MenuContentPage][] => {
    if (!dobbleMenu.length) {
        return [buildEmptyStyledSpread(config)];
    }

    const lastSpread = dobbleMenu[dobbleMenu.length - 1];
    const lastBack = lastSpread[1];

    if (lastBack.elements.length > 0) {
        return [...dobbleMenu, buildEmptyStyledSpread(config)];
    }

    return dobbleMenu;
};

export const flattenVisitorMenu = (
    dobbleMenu: [MenuContentPage, MenuContentPage][]
): MenuContentPage[] => {
    if (!dobbleMenu.length) {
        return [emptyMenuPage, emptyMenuPage];
    }

    const flatMenu: MenuContentPage[] = [];

    dobbleMenu.forEach((group, index) => {
        const [front, back] = group;

        if (index === 0) {
            flatMenu.push(back);
        } else {
            flatMenu.push(front, back);
        }
    });

    return flatMenu;
};


export interface CodexHandlers {
    onValidHex: (hexWithHash: string) => void;
    setCurrentCodex: React.Dispatch<React.SetStateAction<string>>;
    setLastCodex: React.Dispatch<React.SetStateAction<string>>;
}

export const handleCodexChangeValue = (
    rawValue: string,
    handlers: CodexHandlers
) => {
    const value = sanitizeHexInput(rawValue);

    if (isValidHex6(value)) {
        const hex = `#${value}`;
        handlers.onValidHex(hex);
        handlers.setLastCodex(hex);
    }

    handlers.setCurrentCodex(value);
};

export const handleCodexBlurValue = (
    rawValue: string,
    lastCodex: string,
    handlers: CodexHandlers
) => {
    const value = sanitizeHexInput(rawValue);

    if (isValidHex6(value)) {
        const hex = `#${value}`;
        handlers.onValidHex(hex);
        handlers.setLastCodex(hex);
        handlers.setCurrentCodex(value);
    } else {
        handlers.setCurrentCodex(buildHexInputValue(lastCodex));
    }
};

export const stripTime = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const normalizeDate = (value?: Date | string | null): Date | null => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};
export const normalizeDisplayOrders = (
  menus: contentMenuList[],
  daily: typeDailyMenu
): { menus: contentMenuList[]; daily: typeDailyMenu } => {
  const merged: DisplayMenuItem[] = [
    ...menus.map((menu, index) => ({
      type: "menu" as const,
      data: menu,
      sourceIndex: index,
    })),
    ...(daily && daily.image
      ? [{
          type: "daily" as const,
          data: daily,
          sourceIndex: null,
        }]
      : []),
  ].sort((a, b) => {
    const aOrder = a.data.display_order ?? 0;
    const bOrder = b.data.display_order ?? 0;

    if (aOrder !== bOrder) return aOrder - bOrder;

    if (a.type === "daily" && b.type === "menu") return -1;
    if (a.type === "menu" && b.type === "daily") return 1;

    return 0;
  });

  const nextMenus: contentMenuList[] = [];
  let nextDaily = daily;

  merged.forEach((item, index) => {
    const nextOrder = index + 1;

    if (item.type === "menu") {
      nextMenus.push({
        ...item.data,
        display_order: nextOrder,
      });
    } else {
      nextDaily = {
        ...item.data,
        display_order: nextOrder,
      };
    }
  });

  return {
    menus: nextMenus,
    daily: nextDaily,
  };
};
export const createLocalUid = () => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};
export const buildUpdatedMenusWithCreatedIds = (
    menus: contentMenuList[],
    createdItems: { local_uid: string | null; id: number }[]
) => {
    if (!Array.isArray(createdItems) || createdItems.length === 0) return menus;

    return menus.map((item) => {
        if (item.id != null || !item.local_uid) return item;

        const found = createdItems.find(
            (created) => created.local_uid && created.local_uid === item.local_uid
        );

        if (!found) return item;

        return {
            ...item,
            id: found.id,
        };
    });
};